import { z } from 'zod';

/**
 * Customer.io write failures — the report on a person/event write that did not
 * land.
 *
 * Every Customer.io write is fire-and-forget by design (`void cioIdentify(...)`):
 * a marketing sync must never fail a signup, a webhook or a mutation. The cost
 * of that contract was that a failed write was a `console.warn` and nothing
 * else, so a profile that never got `role` or `is_paying` (the Owner-gated paying
 * alert and the sequences read both) left no trace anywhere. One event per
 * failed write is the signal a monitor can alert on; in steady state it is
 * silent.
 *
 * The write is emitted from the single chokepoint (`cioFetch`), so every caller
 * is covered without each one catching. Deliberate skips (internal addresses,
 * non-production env, missing keys) are NOT failures and do not emit.
 */

/** Which Customer.io surface the write targeted. Never the address itself. */
export const CUSTOMERIO_WRITE_TARGETS = [
  /** `/customers/{email}`: a person upsert or attribute write. */
  'customer',
  /** `/customers/{email}/events` or `/events`: an event send. */
  'event',
  /** Anything else under the Track API. */
  'other',
] as const;

/** Why the write did not land. */
export const CUSTOMERIO_WRITE_FAILURE_REASONS = [
  /** Customer.io answered with a non-2xx status. */
  'http_error',
  /** The request threw or timed out before an answer. */
  'network_error',
] as const;

export type CustomerioWriteTarget = (typeof CUSTOMERIO_WRITE_TARGETS)[number];
export type CustomerioWriteFailureReason =
  (typeof CUSTOMERIO_WRITE_FAILURE_REASONS)[number];

export const customerioSchemas = {
  /**
   * A Customer.io write returned an error or threw. `status` is present for
   * `http_error` only. `target` and `method` say what was being written without
   * carrying the address (PII) in the payload.
   */
  'customerio.write_failed': z.object({
    method: z.enum(['PUT', 'POST', 'DELETE']),
    reason: z.enum(CUSTOMERIO_WRITE_FAILURE_REASONS),
    status: z.number().int().optional(),
    target: z.enum(CUSTOMERIO_WRITE_TARGETS),
  }),
};
